
import Vue from "vue";
import Component from "vue-class-component";
import {factory, PracticePeriod, ResultError, PracticesClient} from "@/api-client";
import {BModal} from "bootstrap-vue";
import {Prop} from "vue-property-decorator";
import CheckboxInput from "@/features/shared/components/CheckboxInput.vue";
import LabelOptional from "@/features/shared/components/LabelOptional.vue";
import toasts from "@/components/Toasts";
import moment from "moment";
import DropdownSearch from "@/components/DropdownSearch.vue";

@Component({
    components: {
        CheckboxInput,
        LabelOptional,
        DropdownSearch
    }
})
export default class PracticePeriodModal extends Vue {
    @Prop({default: () => false})
    withNavigationalAndEngineRoomWatch!: boolean;
    @Prop({default: () => false})
    withVoyageCount!: boolean;
    @Prop({default: () => false})
    withNotes!: boolean;
    @Prop({ default: () => false })
    canEdit!: boolean;
    @Prop({default: () => false})
    isCountBased!: boolean;
    withNavigationalWatch = false;
    withEngineRoomWatch = false;
    
    instructorOptions: Array<{value: string, text: string}> = [];
    instructorNameMap: {[key: string]: string} = {}; // Map of instructor IDs to names
    inOutOptions = [
        {text: this.$ls.Documents_Practices_InOutFlag_In, value: true},
        {text: this.$ls.Documents_Practices_InOutFlag_Out, value: false}
    ];

    @Prop({default: () => null})
    selectedIssued?: Date;

    model: PracticePeriod = factory.create(PracticePeriod);
    isEditing = false;
    index?:number;

    validationErrors: ResultError = factory.create(ResultError);

    $refs!: { bModal: BModal };

    async mounted() {
        this.reset();
        this.$refs.bModal.$on("hide", () => this.reset());
        
        // Load instructor options for pilotage practices
        if (this.isCountBased) {
            // Load seafarers with pilot certificates
            // This would need an API endpoint to get seafarers with valid pilot certificates
            // For now, we'll use a placeholder
            await this.loadInstructorOptions();
        }
    }
    
    private async loadInstructorOptions() {
        try {
            // Load seafarers with valid pilot certificates
            const instructors = await new PracticesClient().getPilotInstructors();
            
            // Add empty option at the beginning - use empty string for null value
            this.instructorOptions = [
                {value: '', text: '-'},
                ...instructors.map((i: {value: string, text: string}) => ({
                    value: i.value,
                    text: i.text
                }))
            ];
            
            // Build instructor name map for display in table
            this.instructorNameMap = {};
            instructors.forEach((i: {value: string, text: string}) => {
                this.instructorNameMap[i.value] = i.text;
            });
            
            // Emit the instructor map to parent component
            this.$emit('instructor-map-updated', this.instructorNameMap);
        } catch (error) {
            // In case of error, provide empty option only
            this.instructorOptions = [{value: '', text: '-'}];
            this.instructorNameMap = {};
        }
    }

    private positiveAction()
    {
        if(this.isEditing){
            this.editPractice();
            return;
        }
        this.addPractice();
    }

    private addPractice() {
        // Always validate when adding
        this.validatePractice(this.model);

        if (this.validationErrors.isEmpty) {
            this.$emit("add", this.model);
            this.hideModal();
            toasts.success(this.$ls.Global_Toast_AddSuccess);
        }
        else {
            this.setValidationErrors(this.validationErrors, this.$ls.Global_Toast_FormAddFailure);
        }
    }

    private editPractice() {
        this.validatePractice(this.model);

        if (this.validationErrors.isEmpty) {
            this.$emit("edit", this.model, this.index);
            this.hideModal();
            toasts.success(this.$ls.Global_Toast_UpdateSuccess);
        }
        else {
            this.setValidationErrors(this.validationErrors, this.$ls.Global_Toast_FormAddFailure);
        }
    }

    private hideModal() {
        this.$refs.bModal.hide();
    }
    private updateDurationInDays() {
        if (!this.isCountBased) {
            this.model.durationInDays = this.computedDurationInDays;
        }
    }
    private reset() {
        this.index = undefined;
        this.validationErrors.clear();
        this.model = factory.create(PracticePeriod);
        this.withNavigationalWatch = false;
        this.withEngineRoomWatch = false;
        this.isEditing = false;
    }

    private get computedDurationInDays(): number | undefined {
        if (this.model.endedOn && this.model.startedOn && this.model.endedOn.getTime() >= this.model.startedOn.getTime()) {
            return moment(this.model.endedOn).diff(moment(this.model.startedOn), "days") + 1;
        }
        else {
            return undefined
        }
    }

    private validatePractice(practice: PracticePeriod) {
        this.validationErrors.clear();

        if (!practice.startedOn)
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddPracticePeriod_Error_StartingDateIsMandatory, 'startedOn');

        if (!practice.endedOn)
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddPracticePeriod_Error_EndingDateIsMandatory, 'endedOn');

        if (!this.isCountBased && practice.durationInDays == null)
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddPracticePeriod_Error_DurationIsMandatory, 'durationInDays');

        if (this.withNavigationalWatch && !practice.navigationalWatchDays)
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddPracticePeriod_Error_NavigationalWatchDaysAreMandatory, 'navigationalWatchDays');

        if (this.withEngineRoomWatch && !practice.engineRoomWatchDays)
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddPracticePeriod_Error_EngineRoomWatchDaysAreMandatory, 'engineRoomWatchDays');

        // Validate voyage count is mandatory when withVoyageCount is true
        if (this.withVoyageCount && (practice.voyageCount == null || practice.voyageCount === undefined))
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddPracticePeriod_Error_VoyageCountIsMandatory, 'voyageCount');

        if (this.validationErrors.hasMessages())
            return;

        if (!this.isCountBased && practice.durationInDays && practice.durationInDays < 1)
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddPracticePeriod_Error_MinimumDurationIs1Day, 'durationInDays');

        if (practice.endedOn && practice.startedOn && practice.endedOn < practice.startedOn)
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddPracticePeriod_Error_StartingMustBeLaterThanEnding, 'startedOn');

        // Check if dates are in the future
        const today = new Date();
        today.setHours(23, 59, 59, 999); // Set to end of today to allow today's date
        
        if (practice.startedOn && practice.startedOn > today)
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddPracticePeriod_Error_StartingDateCannotBeInFuture || 'Start date cannot be in the future', 'startedOn');
        
        if (practice.endedOn && practice.endedOn > today)
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddPracticePeriod_Error_EndingDateCannotBeInFuture || 'End date cannot be in the future', 'endedOn');

        if(this.selectedIssued){
            if(practice.startedOn && practice.startedOn > this.selectedIssued){
                this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddPracticePeriod_Error_StartingDateIsAfterIssuedDate, 'startedOn');
            }
            if(practice.endedOn && practice.endedOn > this.selectedIssued){
                this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddPracticePeriod_Error_EndingDateIsAfterIssuedDate, 'endedOn');
            }
        }
        if (this.validationErrors.hasMessages())
            return;

        if (!this.isCountBased && practice.durationInDays && this.computedDurationInDays && practice.durationInDays > this.computedDurationInDays && !this.isEditing)
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddPracticePeriod_Error_TotalDaysIsMoreThanPracticePeriod, 'durationInDays');

        if (!this.isCountBased && !!practice.navigationalWatchDays && practice.durationInDays && practice.navigationalWatchDays > practice.durationInDays)
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddPracticePeriod_Error_NavDaysIsMoreThanTotalDuration, 'navigationalWatchDays');

        if (!this.isCountBased && !!practice.engineRoomWatchDays && practice.engineRoomWatchDays > (practice.durationInDays || 0))
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddPracticePeriod_Error_EngDaysIsMoreThanTotalDuration, 'engineRoomWatchDays');

        if (this.withVoyageCount && practice.voyageCount && practice.voyageCount < 1)
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddPracticePeriod_Error_VoyageCountMinimum || 'Voyage count must be at least 1', 'voyageCount');
    }

    showModal(data:PracticePeriod, index:number) {
      this.isEditing = true;
      this.index = index;
      this.model = data;      
      this.$refs.bModal.show();
    }
}
